export type ProjectDataReferenceTarget =
  | {
      kind: "itineraryDay";
      dayId?: string;
      dayIndex?: number;
      dayNumber?: number;
      source: string;
    }
  | {
      kind: "itineraryItem";
      dayIndex?: number;
      dayNumber?: number;
      itemId?: string;
      itemIndex?: number;
      itemType?: string;
      source: string;
    };

export function parseProjectDataReference(
  source: string | null | undefined,
): ProjectDataReferenceTarget | null {
  if (!source) return null;
  const normalized = source.trim().replace(/^\$\./, "");
  if (!normalized) return null;

  const itemId = parseItineraryItemId(normalized);
  if (itemId) {
    return {
      kind: "itineraryItem",
      itemId,
      ...parseDaySelector(normalized),
      itemType: parseItineraryItemType(normalized),
      source,
    };
  }

  const projectItemPathMatch = normalized.match(
    /(?:^|[^A-Za-z0-9_])(?:project\.)?itineraries\[(\d+)\]\.itinerary_items(?:\[(\d+|\?)\])?(?:\.(.+))?/i,
  );
  if (projectItemPathMatch) {
    const dayIndex = Number.parseInt(projectItemPathMatch[1], 10);
    const itemIndex =
      projectItemPathMatch[2] && projectItemPathMatch[2] !== "?"
        ? Number.parseInt(projectItemPathMatch[2], 10)
        : undefined;
    return {
      kind: "itineraryItem",
      dayIndex,
      itemIndex,
      itemType:
        parseItineraryItemType(projectItemPathMatch[3] ?? "") ??
        parseItineraryItemType(normalized),
      source,
    };
  }

  const strictItemMatch = normalized.match(
    /(?:^|[^A-Za-z0-9_])itineraries\[(\d+)\]\.itinerary_items\[(\d+)\]/i,
  );
  if (strictItemMatch) {
    return {
      kind: "itineraryItem",
      dayIndex: Number.parseInt(strictItemMatch[1], 10),
      itemIndex: Number.parseInt(strictItemMatch[2], 10),
      source,
    };
  }

  if (
    /\bitinerary_items?\b/i.test(normalized) ||
    /\bItinerary\s+Day\s+\d+.*\b(hotel|experience|activity|guide|hire\s*car|transportation|restaurant|other)\b/i.test(
      normalized,
    )
  ) {
    const day = parseDaySelector(normalized);
    const itemType = parseItineraryItemType(normalized);
    if (day.dayIndex != null || day.dayNumber != null || itemType) {
      return {
        kind: "itineraryItem",
        ...day,
        itemType,
        source,
      };
    }
  }

  const strictDayMatch = normalized.match(
    /(?:^|[^A-Za-z0-9_])(?:project\.)?itineraries\[(\d+)\]/i,
  );
  if (strictDayMatch) {
    return {
      kind: "itineraryDay",
      dayIndex: Number.parseInt(strictDayMatch[1], 10),
      source,
    };
  }

  const day = parseDaySelector(normalized);
  if (day.dayIndex != null || day.dayNumber != null) {
    return {
      kind: "itineraryDay",
      ...day,
      source,
    };
  }

  const itineraryDayIdMatch = normalized.match(/^itinerary_day\[(\d+)\]/i);
  if (itineraryDayIdMatch) {
    return {
      kind: "itineraryDay",
      dayId: itineraryDayIdMatch[1],
      source,
    };
  }

  return null;
}

export function hasProjectDataReference(
  source: string | null | undefined,
): boolean {
  return parseProjectDataReference(source) != null;
}

function parseItineraryItemId(source: string): string | null {
  const selectorMatch = source.match(
    /\bitinerary_items\[\s*id\s*=\s*["']?([^\]"'\s;,)]+)["']?\s*\]/i,
  );
  if (selectorMatch) return selectorMatch[1];

  const labelMatch = source.match(/\bItinerary Item ID\s+([^\s,)]+)/i);
  if (labelMatch) return labelMatch[1];

  const idMatch = source.match(/\bid\s*:\s*([0-9]+)/i);
  return idMatch?.[1] ?? null;
}

function parseDaySelector(source: string): {
  dayIndex?: number;
  dayNumber?: number;
} {
  const dayIndexMatch = source.match(
    /(?:^|[^A-Za-z0-9_])(?:project\.)?itineraries\[(\d+)\]/i,
  );
  if (dayIndexMatch) {
    return { dayIndex: Number.parseInt(dayIndexMatch[1], 10) };
  }

  const dayNumberMatch =
    source.match(/\bDay\s*([0-9]+)/i) ??
    source.match(/\bDay([0-9]+)/i) ??
    source.match(/\bitinerary_day\s+([0-9]+)/i) ??
    source.match(/\bday_number\s+([0-9]+)/i);
  if (dayNumberMatch) {
    return { dayNumber: Number.parseInt(dayNumberMatch[1], 10) };
  }

  return {};
}

function parseItineraryItemType(source: string): string | undefined {
  const directMatch = source.match(/\b(Itinerary[A-Za-z]+)\b/);
  if (directMatch) return directMatch[1];

  const bracketMatch = source.match(/\bitinerary_items\[\s*([^\]]+?)\s*\]/i);
  const bracketType = normalizeItemType(bracketMatch?.[1]);
  if (bracketType) return bracketType;

  const dayTypeMatch = source.match(
    /\bDay\s*[0-9]+[^A-Za-z0-9_]+(hotel|experience|activity|guide|hire\s*car|car|transportation|restaurant|other)\b/i,
  );
  const dayType = normalizeItemType(dayTypeMatch?.[1]);
  if (dayType) return dayType;

  const detailTypeMatch = source.match(
    /\bdetail\.(hotel|experience|activity|transportation|restaurant)\b/i,
  );
  const detailType = normalizeItemType(detailTypeMatch?.[1]);
  if (detailType) return detailType;

  return normalizeItemType(source);
}

function normalizeItemType(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const normalized = value.toLowerCase().replace(/[^a-z]/g, "");
  const map: Record<string, string> = {
    activity: "ItineraryActivity",
    car: "ItineraryHireCar",
    experience: "ItineraryExperience",
    guide: "ItineraryGuide",
    hirecar: "ItineraryHireCar",
    hotel: "ItineraryHotel",
    other: "ItineraryOther",
    restaurant: "ItineraryRestaurant",
    transportation: "ItineraryTransportation",
  };
  return map[normalized];
}
